import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import { AlertTriangle, Camera, ChevronRight, X, Check } from "lucide-react";

const ISSUE_CATEGORIES = ["Safety", "Security", "Cleaning", "Lighting", "HVAC", "Music or Audio", "Wi-Fi or Network", "TV or Digital Display", "Hologram Fan", "Stock or Merchandising", "Maintenance", "Other"];
const URGENCIES = ["low", "medium", "high", "emergency"];

const DECLARATION = {
  opening: "I confirm that I have completed the store-opening inspection and that the store is safe, operational, clean, and ready for customers, except for any issues documented in this report.",
  closing: "I confirm that I have completed the store-closing inspection, secured all cash and required areas, shut down the listed systems, armed the security alarm, and confirmed that the store is fully secured, except for any issues documented in this report.",
};

export default function Checklist({ type }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [startTime] = useState(new Date().toISOString());
  const [actualTime, setActualTime] = useState("");
  const [comments, setComments] = useState("");
  const [signature, setSignature] = useState(null);
  const [showIssue, setShowIssue] = useState(null);
  const [showUnable, setShowUnable] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pos, setPos] = useState({ tills_closed: 0, expected_cash: 0, actual_cash: 0, explanation: "", verified_by: "" });
  const [overnightHvac, setOvernightHvac] = useState("");
  const [busy, setBusy] = useState(false);

  const storageKey = `hos-draft-${type}`;

  useEffect(() => {
    api.get(`/checklists/${type}`).then(({ data }) => setTemplate(data));
    try {
      const draft = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (draft) {
        setResponses(draft.responses || {});
        setComments(draft.comments || "");
        setActualTime(draft.actualTime || "");
        setPos(draft.pos || pos);
        setOvernightHvac(draft.overnightHvac || "");
      }
    } catch (err) {
      console.warn("Failed to restore checklist draft", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({ responses, comments, actualTime, pos, overnightHvac }));
    // eslint-disable-next-line
  }, [responses, comments, actualTime, pos, overnightHvac]);

  const sections = useMemo(() => {
    if (!template) return [];
    const map = {};
    template.items.forEach((it) => {
      const s = it.section || "Tasks";
      (map[s] = map[s] || []).push(it);
    });
    return Object.entries(map);
  }, [template]);

  const totalItems = template?.items?.length || 0;
  const doneItems = Object.values(responses).filter((r) => r.completed || r.unable_reason).length;
  const pct = totalItems ? Math.round(100 * doneItems / totalItems) : 0;
  const outstanding = totalItems - doneItems;

  const toggle = (item, val) => {
    setResponses((prev) => ({
      ...prev,
      [item.id]: {
        item_id: item.id,
        completed: val,
        completed_by: user.id,
        completed_by_name: user.name,
        completed_at: val ? new Date().toISOString() : null,
        notes: prev[item.id]?.notes || "",
        photo: prev[item.id]?.photo || null,
        issue_id: prev[item.id]?.issue_id || null,
        unable_reason: null,
      },
    }));
  };

  const setNotes = (id, notes) => {
    setResponses((prev) => ({ ...prev, [id]: { ...(prev[id] || { item_id: id, completed: false }), notes } }));
  };

  const uploadPhoto = async (item, file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setResponses((prev) => ({ ...prev, [item.id]: { ...(prev[item.id] || { item_id: item.id }), photo: reader.result } }));
      toast.success("Photo attached");
    };
    reader.readAsDataURL(file);
  };

  const submitIssue = async (item, form) => {
    try {
      const { data } = await api.post("/issues", { ...form, task_id: item.id });
      setResponses((prev) => ({
        ...prev,
        [item.id]: { ...(prev[item.id] || { item_id: item.id }), issue_id: data.id },
      }));
      toast.success(`Issue ${data.issue_number} logged`);
      setShowIssue(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canSubmit = () => {
    if (!template) return false;
    if (user.role === "employee") return false;
    for (const it of template.items) {
      if (it.required) {
        const r = responses[it.id];
        if (!r || (!r.completed && !r.unable_reason)) return false;
      }
    }
    return !!signature;
  };

  const doSubmit = async () => {
    if (!canSubmit()) { toast.error("Missing required tasks or signature"); return; }
    setBusy(true);
    try {
      const payload = {
        type, start_time: startTime,
        actual_time: actualTime ? new Date(actualTime).toISOString() : new Date().toISOString(),
        manager_comments: comments,
        signature, signature_name: user.name,
        signature_employee_id: user.employee_id || "",
        responses: template.items.map((it) => responses[it.id] || { item_id: it.id, completed: false }),
        pos_reconciliation: type === "closing" ? {
          ...pos,
          difference: Number(pos.actual_cash || 0) - Number(pos.expected_cash || 0),
          expected_cash: Number(pos.expected_cash || 0),
          actual_cash: Number(pos.actual_cash || 0),
          tills_closed: Number(pos.tills_closed || 0),
        } : null,
        overnight_hvac: type === "closing" ? overnightHvac : null,
      };
      const { data } = await api.post("/reports/submit", payload);
      localStorage.removeItem(storageKey);
      toast.success(`Report ${data.report_number} submitted`);
      nav(`/reports/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!template) return <div className="text-[color:var(--text-secondary)]">Loading checklist…</div>;

  return (
    <div className="space-y-6" data-testid={`checklist-${type}`}>
      <div>
        <div className="label-caps">House of Spells — Times Square</div>
        <h1 className="font-heading text-3xl sm:text-4xl mt-1">
          {type === "opening" ? "Store Opening Checklist" : "Store Closing Checklist"}
        </h1>
        <div className="text-[color:var(--text-secondary)] text-sm mt-1">
          Started {new Date(startTime).toLocaleString()} · Manager: {user.name} ({user.employee_id || "—"})
        </div>
      </div>

      <div className="hos-card p-4 sm:p-6 flex flex-wrap items-center gap-6">
        <div>
          <div className="label-caps">Progress</div>
          <div className="font-heading text-3xl">{pct}%</div>
        </div>
        <div>
          <div className="label-caps">Completed</div>
          <div className="text-2xl">{doneItems} / {totalItems}</div>
        </div>
        <div>
          <div className="label-caps">Outstanding</div>
          <div className={`text-2xl ${outstanding > 0 ? "text-[color:var(--warning)]" : ""}`}>{outstanding}</div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="h-2 bg-[color:var(--bg-base)] rounded-full overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--gold), var(--amber))" }} />
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Actual {type === "opening" ? "Open" : "Close"} Time</label>
          <input type="datetime-local" className="hos-input py-2" value={actualTime}
                 onChange={(e) => setActualTime(e.target.value)} data-testid="actual-time-input" />
        </div>
      </div>

      {sections.map(([section, items]) => (
        <div key={section} className="space-y-3">
          <h2 className="font-heading text-2xl text-[color:var(--gold)]">{section}</h2>
          {items.map((it) => {
            const r = responses[it.id] || {};
            const cls = r.completed ? "done" : r.issue_id ? "issue" : "";
            return (
              <div key={it.id} className={`checklist-row ${cls}`} data-testid={`task-${it.id}`}>
                <input
                  type="checkbox" className="hos-checkbox"
                  checked={!!r.completed}
                  onChange={(e) => toggle(it, e.target.checked)}
                  data-testid={`task-check-${it.id}`}
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base sm:text-lg">{it.order}. {it.title}</div>
                      {r.completed_at && <div className="text-xs text-[color:var(--text-secondary)] mt-1">Completed {new Date(r.completed_at).toLocaleTimeString()} by {r.completed_by_name}</div>}
                      {r.unable_reason && <div className="text-xs text-[color:var(--warning)] mt-1">Unable: {r.unable_reason}</div>}
                      {r.issue_id && <div className="text-xs text-[color:var(--error)] mt-1">Issue logged</div>}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <label className="p-2 rounded-md text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] hover:bg-[color:var(--bg-surface-2)] cursor-pointer" data-testid={`task-photo-${it.id}`}>
                        <Camera size={16}/>
                        <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadPhoto(it, e.target.files[0])} />
                      </label>
                      <button className="p-2 rounded-md text-[color:var(--warning)] hover:bg-[color:var(--bg-surface-2)]" title="Report issue" onClick={() => setShowIssue(it)} data-testid={`task-issue-${it.id}`}>
                        <AlertTriangle size={16}/>
                      </button>
                      <button className="p-2 rounded-md text-[color:var(--text-secondary)] hover:text-[color:var(--warning)] hover:bg-[color:var(--bg-surface-2)]" title="Unable to complete" onClick={() => setShowUnable(it)} data-testid={`task-unable-${it.id}`}>
                        <X size={16}/>
                      </button>
                    </div>
                  </div>
                  <input
                    type="text" placeholder="Notes (optional)"
                    className="hos-input mt-2 text-sm py-2"
                    value={r.notes || ""}
                    onChange={(e) => setNotes(it.id, e.target.value)}
                    data-testid={`task-notes-${it.id}`}
                  />
                  {r.photo && <img src={r.photo} alt="attached" className="mt-2 h-24 rounded-md border border-[color:var(--border)]" />}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {type === "closing" && (
        <div className="hos-card p-6 space-y-4">
          <h3 className="font-heading text-2xl">POS Reconciliation</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumField label="Tills closed" value={pos.tills_closed} onChange={(v) => setPos({ ...pos, tills_closed: v })} testid="pos-tills"/>
            <NumField label="Expected cash ($)" value={pos.expected_cash} onChange={(v) => setPos({ ...pos, expected_cash: v })} testid="pos-expected"/>
            <NumField label="Actual cash ($)" value={pos.actual_cash} onChange={(v) => setPos({ ...pos, actual_cash: v })} testid="pos-actual"/>
            <div>
              <label className="label-caps">Difference</label>
              <div className={`hos-input ${Number(pos.actual_cash - pos.expected_cash) !== 0 ? "text-[color:var(--warning)]" : ""}`}>
                ${(Number(pos.actual_cash || 0) - Number(pos.expected_cash || 0)).toFixed(2)}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="label-caps">Explanation for difference</label>
              <input className="hos-input" value={pos.explanation} onChange={(e) => setPos({ ...pos, explanation: e.target.value })} data-testid="pos-explanation"/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-caps">Verified by</label>
              <input className="hos-input" value={pos.verified_by} onChange={(e) => setPos({ ...pos, verified_by: e.target.value })} data-testid="pos-verifier"/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-caps">Overnight HVAC setting</label>
              <input className="hos-input" placeholder="e.g. 65°F" value={overnightHvac} onChange={(e) => setOvernightHvac(e.target.value)} data-testid="hvac-overnight"/>
            </div>
          </div>
        </div>
      )}

      <div className="hos-card p-6 space-y-4">
        <label className="label-caps">Manager Comments</label>
        <textarea className="hos-input min-h-[100px]" value={comments} onChange={(e) => setComments(e.target.value)} data-testid="manager-comments"/>
        <SignaturePad onChange={setSignature} />
        <div className="text-sm text-[color:var(--text-secondary)] italic">"{DECLARATION[type]}"</div>
        <div className="flex flex-wrap gap-3 justify-end">
          <button className="hos-btn-outline" onClick={() => nav("/dashboard")} data-testid="cancel-btn">Save & Exit</button>
          <button className="hos-btn-primary" onClick={() => setReviewOpen(true)} disabled={busy} data-testid="review-btn">
            Review & Submit <ChevronRight size={16} className="inline"/>
          </button>
        </div>
      </div>

      {showIssue && <IssueModal item={showIssue} onClose={() => setShowIssue(null)} onSubmit={(f) => submitIssue(showIssue, f)} />}
      {showUnable && <UnableModal item={showUnable} onClose={() => setShowUnable(null)} onSubmit={(reason) => {
        setResponses((prev) => ({ ...prev, [showUnable.id]: { ...(prev[showUnable.id] || { item_id: showUnable.id }), completed: false, unable_reason: reason, completed_at: new Date().toISOString(), completed_by: user.id, completed_by_name: user.name } }));
        setShowUnable(null);
      }} />}
      {reviewOpen && <ReviewModal
        type={type} pct={pct} done={doneItems} outstanding={outstanding} template={template} responses={responses}
        actualTime={actualTime} comments={comments} pos={pos} signature={signature}
        onClose={() => setReviewOpen(false)}
        onConfirm={doSubmit} busy={busy} canSubmit={canSubmit()}
      />}
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-caps">{label}</label>
      <input type="number" className="hos-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>
    </div>
  );
}

function IssueModal({ item, onClose, onSubmit }) {
  const [f, setF] = useState({ category: ISSUE_CATEGORIES[0], description: "", urgency: "medium", action_taken: "", follow_up_required: false });
  return (
    <Modal title={`Report Issue — ${item.title}`} onClose={onClose}>
      <div className="grid gap-3">
        <select className="hos-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="issue-category">
          {ISSUE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <textarea className="hos-input min-h-[80px]" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="issue-description"/>
        <select className="hos-input" value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} data-testid="issue-urgency">
          {URGENCIES.map((u) => <option key={u} value={u}>{u.toUpperCase()}</option>)}
        </select>
        <input className="hos-input" placeholder="Action taken" value={f.action_taken} onChange={(e) => setF({ ...f, action_taken: e.target.value })} data-testid="issue-action"/>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.follow_up_required} onChange={(e) => setF({ ...f, follow_up_required: e.target.checked })}/>
          Management follow-up required
        </label>
      </div>
      <div className="flex justify-end gap-3 mt-4">
        <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
        <button className="hos-btn-primary" onClick={() => onSubmit(f)} data-testid="issue-submit">Log Issue</button>
      </div>
    </Modal>
  );
}

function UnableModal({ item, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title={`Unable to Complete — ${item.title}`} onClose={onClose}>
      <textarea className="hos-input min-h-[100px]" placeholder="Explain why, corrective action, and follow-up..." value={reason} onChange={(e) => setReason(e.target.value)} data-testid="unable-reason"/>
      <div className="flex justify-end gap-3 mt-4">
        <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
        <button className="hos-btn-primary" onClick={() => onSubmit(reason)} data-testid="unable-submit" disabled={!reason.trim()}>Mark Unable</button>
      </div>
    </Modal>
  );
}

function ReviewModal({ type, pct, done, outstanding, template, responses, actualTime, comments, pos, signature, onClose, onConfirm, busy, canSubmit }) {
  const issues = Object.values(responses).filter((r) => r?.issue_id).length;
  const incomplete = template.items.filter((it) => it.required && !(responses[it.id]?.completed || responses[it.id]?.unable_reason));
  return (
    <Modal title={`Review ${type === "opening" ? "Opening" : "Closing"} Report`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <Info label="Completed" value={`${done} / ${template.items.length}`} />
        <Info label="Outstanding" value={String(outstanding)} />
        <Info label="Issues" value={String(issues)} />
        <Info label="Completion" value={`${pct}%`} />
        <Info label={`Actual ${type === "opening" ? "Open" : "Close"}`} value={actualTime ? new Date(actualTime).toLocaleString() : "Now"} />
        <Info label="Signature" value={signature ? "Captured" : "Missing"} />
      </div>
      {type === "closing" && (
        <div className="mt-4 p-3 rounded-lg bg-[color:var(--bg-base)] border border-[color:var(--border)] text-sm">
          <div className="label-caps mb-1">POS</div>
          Diff: ${(Number(pos.actual_cash || 0) - Number(pos.expected_cash || 0)).toFixed(2)} · Tills: {pos.tills_closed}
        </div>
      )}
      {incomplete.length > 0 && (
        <div className="mt-4 p-3 rounded-lg border border-[color:var(--warning)] bg-[color:var(--warning)]/10 text-sm">
          <div className="font-semibold text-[color:var(--warning)] mb-1">Incomplete required tasks:</div>
          <ul className="list-disc pl-5">{incomplete.slice(0, 5).map((i) => <li key={i.id}>{i.title}</li>)}</ul>
        </div>
      )}
      {comments && <div className="mt-4 text-sm text-[color:var(--text-secondary)] italic">"{comments}"</div>}
      <div className="flex justify-end gap-3 mt-5">
        <button className="hos-btn-outline" onClick={onClose} disabled={busy}>Back</button>
        <button className="hos-btn-primary" onClick={onConfirm} disabled={!canSubmit || busy} data-testid="final-submit-btn">
          <Check size={16} className="inline mr-1"/> Confirm & Submit
        </button>
      </div>
    </Modal>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="text-lg">{value}</div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[color:var(--text-secondary)]"><X size={20}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
